import { type ReactNode, createContext, useContext, useId, useLayoutEffect } from "react";

/**
 * Where a page's title is drawn. The designer's rule (her CLAUDE.md §4): the
 * shell's top bar carries the page title; there is no big in-page title under
 * it. An app whose shell has such a bar provides a sink here, and a
 * `<ListHeader>` at the page's h1 level hands its title to it instead of
 * drawing an <h1> of its own. Without a provider (another app, a test) the
 * header draws its title as before.
 *
 * `set` is called on every render of the header with that render's title;
 * `clear` when it unmounts. `id` keeps two headers from clobbering each other.
 */
export interface PageTitleSink {
	set: (id: string, title: ReactNode) => void;
	clear: (id: string) => void;
}

const PageTitleContext = createContext<PageTitleSink | null>(null);

export function PageTitleProvider({ sink, children }: { sink: PageTitleSink; children: ReactNode }) {
	return <PageTitleContext.Provider value={sink}>{children}</PageTitleContext.Provider>;
}

/**
 * Hands `title` to the provider's sink while the caller is mounted, when
 * `enabled`. Returns whether it did — i.e. whether the caller must NOT draw
 * the title itself.
 */
export function useHoistedPageTitle(title: ReactNode, enabled = true): boolean {
	const sink = useContext(PageTitleContext);
	const id = useId();
	const hoisted = enabled && sink !== null;
	useLayoutEffect(() => {
		if (hoisted) sink.set(id, title);
	});
	useLayoutEffect(() => {
		if (!hoisted) return;
		return () => sink.clear(id);
	}, [hoisted, sink, id]);
	return hoisted;
}
